/**
 * MIQIntelligenceBrief Component
 * 
 * A transparent explanation of how MIQ is interpreting your profile, behavior, and goals.
 * This is a READ-ONLY intelligence artifact that updates over time.
 * 
 * NOT a chatbot, assistant, or conversational agent.
 * Explains MIQ — does not replace it.
 */

import React, { useState, useEffect } from 'react';
import posthog from 'posthog-js';

const MIQIntelligenceBrief = ({
  userId,
  userProfile,
  aiNutritionGoals,
  metabolicProfile,
  totalMealsLogged,
  miqScore,
  miqConfidence,
  todayMeals,
  onNavigateToInput,
  onFeedbackSubmit,
  onShowToast,  // For non-blocking toast
  BACKEND_URL,
  // Routing handlers for smart navigation (tier-gated in parent)
  onOpenSmartGoals,
  setShowOnboarding,
  setShowDailyCheckin
}) => {
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackType, setFeedbackType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Weekly insights state (dynamic data)
  const [weeklyInsights, setWeeklyInsights] = useState(null);
  const [weeklyInsightsLoading, setWeeklyInsightsLoading] = useState(true);
  
  // Hydration narrative state
  const [hydrationData, setHydrationData] = useState(null);
  const [hydrationEventFired, setHydrationEventFired] = useState(false);
  
  // Fetch 7-day hydration history for narrative eligibility
  useEffect(() => {
    const fetchHydrationHistory = async () => {
      if (!userId || !BACKEND_URL) return;
      
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/ai-miq/daily-checkin/${userId}/hydration-history?days=7`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setHydrationData(data);
          }
        }
      } catch (error) {
        console.error('Error fetching hydration history:', error);
      }
    };
    
    fetchHydrationHistory();
  }, [userId, BACKEND_URL]);
  
  // Fetch weekly insights (dynamic stats + AI tip)
  useEffect(() => {
    const fetchWeeklyInsights = async () => {
      if (!userId || !BACKEND_URL) return;
      
      setWeeklyInsightsLoading(true);
      try {
        const tzOffset = -(new Date().getTimezoneOffset() / 60);
        const response = await fetch(
          `${BACKEND_URL}/api/miq/weekly-insights/${userId}?tz_offset=${tzOffset}`
        );
        if (response.ok) {
          const data = await response.json();
          setWeeklyInsights(data);
        }
      } catch (error) {
        console.error('Error fetching weekly insights:', error);
      } finally {
        setWeeklyInsightsLoading(false);
      }
    };
    
    fetchWeeklyInsights();
  }, [userId, BACKEND_URL]);
  
  // Fire PostHog event when hydration narrative is shown
  useEffect(() => {
    if (hydrationData && hydrationData.days_logged >= 1 && !hydrationEventFired) {
      posthog.capture('miq_weekly_brief_hydration_shown', {
        user_id: userId,
        days_logged: hydrationData.days_logged,
        is_consistent: hydrationData.is_consistent,
        consistency_percent: hydrationData.consistency_percent
      });
      setHydrationEventFired(true);
    }
  }, [hydrationData, userId, hydrationEventFired]);
  
  // Generate hydration narrative based on consistency
  const getHydrationNarrative = () => {
    if (!hydrationData || hydrationData.days_logged < 1) return null;
    
    const { days_logged, is_consistent } = hydrationData;
    
    if (is_consistent) {
      // >= 5 out of 7 days
      return {
        type: 'consistency',
        icon: '💧',
        title: 'Hydration Consistency',
        message: `Great job! You've logged hydration ${days_logged} of the last 7 days. Consistent water intake supports your metabolic health and energy levels.`
      };
    } else if (days_logged >= 1) {
      // 1-4 days
      return {
        type: 'opportunity',
        icon: '💧',
        title: 'Hydration Opportunity',
        message: `You've logged hydration ${days_logged} of the last 7 days. Try to log your water intake more consistently — even small improvements support metabolic efficiency.`
      };
    }
    
    return null;
  };
  
  // Check if we have sufficient data
  const hasGoals = aiNutritionGoals?.intake_complete;
  const hasProfile = userProfile?.height_cm && userProfile?.weight_kg;
  const hasMeals = totalMealsLogged >= 3;
  const hasAncestry = aiNutritionGoals?.intake_data?.ancestry;
  const hasMedicalGuardrails = userProfile?.medical_conditions?.length > 0;
  
  // Get completed fasts from localStorage
  const completedFasts = parseInt(localStorage.getItem(`miq_fasts_completed_${userId}`) || '0', 10);
  const hasIFData = completedFasts > 0;
  
  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 1: What MIQ Is Optimizing For You
  // ═══════════════════════════════════════════════════════════════════════════
  const getOptimizationSummary = () => {
    if (!hasGoals) return null;
    
    const goal = aiNutritionGoals?.nutrition_goals?.goal_emphasis || 'maintenance';
    const pace = aiNutritionGoals?.intake_data?.target_pace || 'moderate';
    const activity = userProfile?.activity_level || 'moderate';
    
    const goalLabels = {
      'fat_loss': 'sustainable fat loss',
      'muscle_gain': 'lean muscle development',
      'maintenance': 'metabolic balance',
      'performance': 'athletic performance',
      'longevity': 'long-term metabolic health'
    };
    
    const paceLabels = {
      'relaxed': 'a gradual, sustainable',
      'moderate': 'a balanced',
      'aggressive': 'an accelerated'
    };
    
    return `MIQ is calibrating your targets for ${goalLabels[goal] || goal} using ${paceLabels[pace] || pace} approach, aligned with your ${activity} activity level.`;
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 2: Top 3 Focus Areas
  // ═══════════════════════════════════════════════════════════════════════════
  const getFocusAreas = () => {
    if (!hasGoals) return [];
    
    const goal = aiNutritionGoals?.nutrition_goals?.goal_emphasis || 'maintenance';
    const ifSchedule = aiNutritionGoals?.nutrition_goals?.if_schedule?.type;
    
    // Define all possible focus areas
    const allFocusAreas = {
      protein_consistency: {
        label: 'Protein Consistency',
        icon: '🥩',
        why: 'Supports muscle preservation during caloric changes and improves satiety.',
        watching: 'Daily protein intake relative to your target, meal-to-meal distribution.'
      },
      carb_timing: {
        label: 'Carb Timing',
        icon: '🍚',
        why: 'Strategic carb placement can optimize energy and recovery.',
        watching: 'When carbs are consumed relative to activity and fasting windows.'
      },
      portion_management: {
        label: 'Portion Management',
        icon: '🍽️',
        why: 'Consistent portions help MIQ predict and refine your targets accurately.',
        watching: 'Meal-to-meal calorie variance and overall daily consistency.'
      },
      late_eating: {
        label: 'Late-Eating Reduction',
        icon: '🌙',
        why: 'Aligning eating with circadian rhythm may improve metabolic outcomes.',
        watching: 'Meal timestamps and eating window boundaries.'
      },
      fiber_micronutrients: {
        label: 'Fiber & Micronutrients',
        icon: '🥗',
        why: 'Adequate fiber supports digestion and micronutrient adequacy fuels metabolic processes.',
        watching: 'Food variety and vegetable/whole food frequency.'
      },
      sodium_moderation: {
        label: 'Sodium Moderation',
        icon: '🧂',
        why: 'Balanced sodium intake supports stable energy and reduces water retention fluctuations.',
        watching: 'Processed food frequency and sodium-heavy meal patterns.'
      },
      recovery_sleep: {
        label: 'Recovery & Sleep',
        icon: '😴',
        why: 'Sleep quality directly impacts metabolic rate and hunger hormones.',
        watching: 'Daily check-in data on sleep and stress levels.'
      }
    };
    
    // Select top 3 based on goal
    let selected = [];
    if (goal === 'fat_loss') {
      selected = ['protein_consistency', 'portion_management', 'late_eating'];
    } else if (goal === 'muscle_gain') {
      selected = ['protein_consistency', 'carb_timing', 'recovery_sleep'];
    } else if (goal === 'performance') {
      selected = ['carb_timing', 'protein_consistency', 'recovery_sleep'];
    } else if (goal === 'longevity') {
      selected = ['fiber_micronutrients', 'sodium_moderation', 'late_eating'];
    } else {
      selected = ['portion_management', 'protein_consistency', 'fiber_micronutrients'];
    }
    
    // If IF is active, swap one focus area
    if (ifSchedule && ifSchedule !== 'none') {
      selected[2] = 'late_eating';
    }
    
    return selected.map(key => allFocusAreas[key]);
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 2.5: PERSONALIZED TIPS (P2 / P2.1 / P3 Polish)
  // Structure: Context → Why this matters → What MIQ is monitoring
  // Tone: Explanatory (MIQ explains decisions), not instructional (no commands)
  // ═══════════════════════════════════════════════════════════════════════════
  const getPersonalizedTips = () => {
    if (!hasGoals) return null;
    
    const tips = [];
    const goal = aiNutritionGoals?.intake_data?.goal_emphasis || 'metabolic_health';
    const timeline = aiNutritionGoals?.intake_data?.goal_timeline;
    const activityLevel = userProfile?.activity_level;
    const mealTiming = aiNutritionGoals?.nutrition_goals?.meal_timing?.preference;
    const mealsPerDay = aiNutritionGoals?.nutrition_goals?.meal_timing?.meals_per_day || '3';
    const bodyType = userProfile?.body_composition?.body_type || metabolicProfile?.somatotype;
    const ifSchedule = aiNutritionGoals?.nutrition_goals?.if_schedule?.type;
    const ancestry = aiNutritionGoals?.intake_data?.ancestry;
    const medicalConditions = userProfile?.medical_conditions || [];
    
    // Labels for readability
    const goalLabels = {
      'fat_loss': 'fat loss',
      'muscle_gain': 'muscle building',
      'performance': 'performance optimization',
      'longevity': 'long-term metabolic health',
      'metabolic_health': 'metabolic balance'
    };
    const goalLabel = goalLabels[goal] || 'metabolic health';
    
    const timelineLabels = {
      '4_weeks': 'a focused 4-week window',
      '8_12_weeks': 'an 8-12 week progression',
      'gradual': 'a gradual, sustainable approach',
      'maintenance': 'ongoing maintenance'
    };
    const timelineLabel = timelineLabels[timeline] || 'your timeline';
    
    const activityLabels = {
      'sedentary': 'lower activity',
      'light': 'light activity',
      'moderate': 'moderate activity',
      'active': 'active lifestyle',
      'very_active': 'high activity demands'
    };
    const activityLabel = activityLabels[activityLevel] || 'your activity level';
    
    const mealTimingLabels = {
      'morning': 'earlier eating',
      'midday': 'midday-centered eating',
      'evening': 'later eating',
      'flexible': 'flexible timing'
    };
    const timingLabel = mealTimingLabels[mealTiming] || 'your meal timing';
    
    // Ancestry context labels (population-level, non-deterministic)
    const ancestryContextLabels = {
      'european': 'population-level tendencies associated with European dietary heritage',
      'east_asian': 'population-level tendencies associated with East Asian dietary patterns',
      'south_asian': 'population-level tendencies associated with South Asian dietary traditions',
      'african': 'population-level tendencies associated with diverse African dietary heritage',
      'middle_eastern': 'population-level tendencies associated with Middle Eastern dietary traditions',
      'latin_american': 'population-level tendencies associated with Latin American dietary heritage',
      'pacific_islander': 'population-level tendencies associated with Pacific Islander heritage',
      'native_american': 'population-level tendencies associated with Indigenous dietary patterns'
    };
    const ancestryLabel = ancestry ? ancestryContextLabels[ancestry] : null;
    
    // ═══════════════════════════════════════════════════════════════════════════
    // TIP 1: Goal + Timeline + Activity (+ Ancestry if available and no medical)
    // Structure: Context → Why → What MIQ monitors
    // ═══════════════════════════════════════════════════════════════════════════
    const hasMedical = medicalConditions.length > 0;
    
    if (ancestry && !hasMedical) {
      // ANCESTRY-ENHANCED TIP 1: Goal + Ancestry + Activity/Timeline
      if (goal === 'fat_loss') {
        tips.push({
          context: `Your ${goalLabel} goal combined with ${ancestryLabel} and ${activityLabel}.`,
          why: 'Portion control and late-day carb moderation tend to support metabolic efficiency more effectively when these factors align.',
          watching: 'MIQ monitors meal-to-meal portion variance and evening carbohydrate timing.'
        });
      } else if (goal === 'muscle_gain') {
        tips.push({
          context: `Your ${goalLabel} focus paired with ${ancestryLabel} and ${activityLabel}.`,
          why: 'Protein distribution and nutrient timing become more impactful when activity demands and heritage patterns are considered together.',
          watching: 'MIQ monitors protein spread across meals and post-activity nutrition timing.'
        });
      } else if (goal === 'performance') {
        tips.push({
          context: `${goalLabel} combined with ${ancestryLabel} and ${timelineLabel}.`,
          why: 'Carbohydrate timing around training windows optimizes energy availability when aligned with your metabolic context.',
          watching: 'MIQ monitors carb placement relative to your most active periods.'
        });
      } else if (goal === 'longevity') {
        tips.push({
          context: `Your ${goalLabel} goal paired with ${ancestryLabel} and ${activityLabel}.`,
          why: 'Sustainable eating patterns and whole food emphasis support long-term metabolic health across diverse backgrounds.',
          watching: 'MIQ monitors food variety, consistency patterns, and overall nutritional balance.'
        });
      } else {
        tips.push({
          context: `MIQ is calibrating for ${goalLabel} using ${ancestryLabel}, ${timelineLabel}, and ${activityLabel}.`,
          why: 'Balanced targets emerge when multiple factors inform the model together.',
          watching: 'MIQ monitors overall consistency and adaptation to your targets.'
        });
      }
    } else {
      // STANDARD TIP 1 (no ancestry or has medical conditions)
      if (goal === 'fat_loss') {
        tips.push({
          context: `Your ${goalLabel} focus within ${timelineLabel} and ${activityLabel}.`,
          why: 'Consistent portion sizes help MIQ predict your needs without aggressive restriction.',
          watching: 'MIQ monitors meal-to-meal calorie variance and overall daily consistency.'
        });
      } else if (goal === 'muscle_gain') {
        tips.push({
          context: `Your ${goalLabel} goal with ${activityLabel} over ${timelineLabel}.`,
          why: 'Spreading protein across meals supports muscle synthesis better than concentrated intake.',
          watching: 'MIQ monitors protein distribution and total daily protein relative to your target.'
        });
      } else if (goal === 'performance') {
        tips.push({
          context: `${goalLabel} with ${activityLabel} over ${timelineLabel}.`,
          why: 'Strategic carbohydrate timing fuels performance while allowing metabolic adaptation.',
          watching: 'MIQ monitors carb timing relative to your activity windows.'
        });
      } else {
        tips.push({
          context: `MIQ is optimizing for ${goalLabel} given ${timelineLabel} and ${activityLabel}.`,
          why: 'Balanced targets without extreme swings support sustainable metabolic health.',
          watching: 'MIQ monitors overall consistency in timing and portions.'
        });
      }
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // TIP 2: Meal Timing + Metabolic Profile + IF
    // Structure: Context → Why → What MIQ monitors
    // ═══════════════════════════════════════════════════════════════════════════
    const ifLabel = ifSchedule?.replace('_', ':') || 'intermittent fasting';
    const hasIF = ifSchedule && ifSchedule !== 'none';
    
    if (bodyType && hasIF) {
      const bodyTypeInsights = {
        'Ectomorph': 'faster metabolic turnover',
        'Mesomorph': 'balanced metabolic profile',
        'Endomorph': 'efficient energy metabolism'
      };
      const bodyInsight = bodyTypeInsights[bodyType] || 'your metabolic characteristics';
      
      if (ancestry && !hasMedical) {
        tips.push({
          context: `Your ${ifLabel} schedule, ${bodyInsight}, ${timingLabel}, and ${ancestryLabel}.`,
          why: 'These factors together shape optimal calorie distribution and highlight sodium/hydration patterns worth monitoring.',
          watching: 'MIQ monitors eating window adherence, protein timing within the window, and sodium patterns.'
        });
      } else {
        tips.push({
          context: `Your ${ifLabel} schedule combined with ${bodyInsight} and ${timingLabel} preference.`,
          why: 'Fasting windows interact with metabolic profile to determine optimal calorie distribution.',
          watching: 'MIQ monitors eating window adherence and nutrient density within the window.'
        });
      }
    } else if (bodyType) {
      const bodyTypeContext = {
        'Ectomorph': {
          context: `Your ${timingLabel} preference and faster metabolic turnover.`,
          why: 'Higher metabolic rates benefit from adequate energy distribution throughout the day.',
          watching: 'MIQ monitors meal frequency and energy adequacy across the day.'
        },
        'Mesomorph': {
          context: `Your ${timingLabel} preference and balanced metabolic profile.`,
          why: 'Balanced metabolism allows standard macro ratios with flexibility for variation.',
          watching: 'MIQ monitors macro balance and meal spacing patterns.'
        },
        'Endomorph': {
          context: `Your ${timingLabel} preference and efficient energy metabolism.`,
          why: 'Efficient metabolisms often respond well to protein emphasis for sustained satiety.',
          watching: 'MIQ monitors protein adequacy at each meal and overall satiety signals.'
        }
      };
      tips.push(bodyTypeContext[bodyType] || {
        context: `Your metabolic profile and ${timingLabel} preference.`,
        why: 'Metabolic characteristics inform how MIQ distributes your calorie targets.',
        watching: 'MIQ monitors macro distribution and meal timing patterns.'
      });
    } else if (ancestry && !hasMedical && hasIF) {
      tips.push({
        context: `Your ${ifLabel} schedule and ${ancestryLabel}.`,
        why: 'Eating window structure interacts with population-level nutritional tendencies to inform protein timing.',
        watching: 'MIQ monitors protein distribution within your eating window.'
      });
    } else {
      tips.push({
        context: `Your ${timingLabel} preference and ${mealsPerDay}-meal structure.`,
        why: 'Meal timing patterns help MIQ optimize when you eat, not just what.',
        watching: 'MIQ monitors meal consistency and timing regularity day-to-day.'
      });
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // TIP 3: Medical Guardrail (priority) OR Fallback
    // Structure: Context → Why → What MIQ monitors
    // ═══════════════════════════════════════════════════════════════════════════
    if (hasMedical) {
      if (medicalConditions.includes('diabetes') || medicalConditions.includes('prediabetes')) {
        tips.push({
          context: `Blood sugar considerations combined with ${activityLabel}.`,
          why: 'Even carb distribution and avoiding aggressive calorie swings support glucose stability.',
          watching: 'MIQ monitors carb distribution across meals and avoids large calorie deficits.'
        });
      } else if (medicalConditions.includes('heart_condition')) {
        tips.push({
          context: `Cardiovascular considerations with ${activityLabel}.`,
          why: 'Sodium awareness and heart-healthy food patterns support cardiovascular wellness.',
          watching: 'MIQ monitors sodium-dense food frequency and overall sodium patterns.'
        });
      } else if (medicalConditions.includes('kidney_condition')) {
        tips.push({
          context: `Kidney considerations alongside ${activityLabel}.`,
          why: 'Conservative protein ceilings and hydration awareness protect kidney function.',
          watching: 'MIQ monitors protein totals against safe ceilings and hydration cues.'
        });
      } else if (medicalConditions.includes('pregnancy') || medicalConditions.includes('breastfeeding')) {
        tips.push({
          context: `Nutritional needs during this important phase with ${activityLabel}.`,
          why: 'Adequate calorie floors and nutrient density support both your needs and development.',
          watching: 'MIQ monitors calorie adequacy and nutrient-dense food frequency.'
        });
      } else {
        tips.push({
          context: `Your health considerations alongside ${activityLabel}.`,
          why: 'Appropriate guardrails ensure MIQ operates within safe boundaries.',
          watching: 'MIQ monitors patterns within guardrail limits and adjusts accordingly.'
        });
      }
    } else {
      tips.push({
        context: `Your ${activityLabel} and ${mealsPerDay}-meal pattern.`,
        why: 'Consistent logging helps MIQ refine energy distribution and target accuracy.',
        watching: 'MIQ monitors logging consistency and pattern stability over time.'
      });
    }
    
    return tips.slice(0, 3);
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 3: Metabolic Profile Context
  // ═══════════════════════════════════════════════════════════════════════════
  const getMetabolicContext = () => {
    const bodyType = userProfile?.body_composition?.body_type || metabolicProfile?.somatotype;
    if (!bodyType) return null;
    
    const contexts = {
      'Ectomorph': {
        characteristic: 'faster metabolic turnover',
        implication: 'MIQ sets slightly higher calorie floors to support energy needs.',
        focus: 'calorie-dense, nutrient-rich foods'
      },
      'Mesomorph': {
        characteristic: 'balanced metabolic responsiveness',
        implication: 'MIQ uses standard macro ratios with flexibility for activity variation.',
        focus: 'balanced macronutrient distribution'
      },
      'Endomorph': {
        characteristic: 'efficient energy storage',
        implication: 'MIQ emphasizes protein-forward ratios and moderate carb timing.',
        focus: 'protein-rich meals with strategic carb placement'
      }
    };
    
    return contexts[bodyType] || null;
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 4: Genetic Context (Ancestry)
  // ═══════════════════════════════════════════════════════════════════════════
  const getGeneticContext = () => {
    if (!hasAncestry) return null;
    
    const ancestry = aiNutritionGoals.intake_data.ancestry;
    
    const ancestryContexts = {
      'european': [
        'European populations show varied lactose tolerance, supporting dairy inclusion if tolerated.',
        'Historically grain-inclusive diets may support moderate carbohydrate metabolism.'
      ],
      'east_asian': [
        'East Asian populations may have lower lactase persistence — MIQ notes dairy alternatives.',
        'Traditional dietary patterns emphasize rice and vegetables, reflected in carb timing suggestions.'
      ],
      'south_asian': [
        'South Asian populations may benefit from attention to refined carb timing and glycemic load.',
        'Traditional spice-rich cuisines offer metabolic benefits — variety is encouraged.'
      ],
      'african': [
        'Diverse African dietary heritage supports flexible macro approaches.',
        'Some populations show strong lactose tolerance; individual response varies.'
      ],
      'middle_eastern': [
        'Middle Eastern dietary patterns align well with Mediterranean-style eating.',
        'Traditional emphasis on legumes and healthy fats is reflected in macro suggestions.'
      ],
      'latin_american': [
        'Latin American dietary diversity supports flexible carbohydrate sources.',
        'Bean and legume emphasis aligns with protein and fiber targets.'
      ],
      'pacific_islander': [
        'Pacific Islander dietary heritage includes nutrient-dense whole foods.',
        'Traditional activity levels suggest attention to portion calibration with modern lifestyles.'
      ],
      'native_american': [
        'Indigenous dietary patterns emphasize whole foods and seasonal eating.',
        'MIQ supports traditional food integration where available.'
      ]
    };
    
    return ancestryContexts[ancestry] || null;
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 5: Meal Timing & Portion Signals
  // ═══════════════════════════════════════════════════════════════════════════
  const getMealTimingInsights = () => {
    if (!hasMeals && !hasIFData) {
      return {
        learning: true,
        message: "MIQ is still learning your timing patterns — log a few more meals to unlock insights."
      };
    }
    
    const insights = [];
    const adjustments = [];
    
    // Generate insights based on available data
    if (hasIFData) {
      insights.push(`You've completed ${completedFasts} fasting windows, establishing a timing rhythm.`);
      if (completedFasts >= 3) {
        adjustments.push("Consider aligning your largest meal with the middle of your eating window.");
      }
    }
    
    if (hasMeals) {
      insights.push(`MIQ has analyzed ${totalMealsLogged} meals to identify your eating patterns.`);
      adjustments.push("Aim for consistent meal times day-to-day for better metabolic predictability.");
    }
    
    // Add IF-specific insight
    const ifSchedule = aiNutritionGoals?.nutrition_goals?.if_schedule;
    if (ifSchedule && ifSchedule.type !== 'none') {
      const ifType = ifSchedule.type?.replace('_', ':') || '16:8';
      insights.push(`Your ${ifType} schedule is being factored into calorie distribution.`);
      adjustments.push("Front-load protein intake earlier in your eating window for better satiety.");
    }
    
    return {
      learning: false,
      insights: insights.slice(0, 2),
      adjustments: adjustments.slice(0, 2)
    };
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 6: Medical Guardrails
  // ═══════════════════════════════════════════════════════════════════════════
  const getMedicalGuardrails = () => {
    if (!hasMedicalGuardrails) return null;
    
    const conditions = userProfile.medical_conditions;
    const guardrails = [];
    
    if (conditions.includes('diabetes') || conditions.includes('prediabetes')) {
      guardrails.push("Because you indicated blood sugar considerations, MIQ prioritizes steady carb distribution and avoids aggressive calorie swings.");
    }
    if (conditions.includes('heart_condition')) {
      guardrails.push("Because you indicated cardiovascular considerations, MIQ monitors sodium patterns and emphasizes heart-healthy food choices.");
    }
    if (conditions.includes('pregnancy') || conditions.includes('breastfeeding')) {
      guardrails.push("Because you indicated maternal health status, MIQ avoids any caloric deficit and prioritizes nutrient density.");
    }
    if (conditions.includes('eating_disorder_history')) {
      guardrails.push("Because you indicated a sensitive relationship with food, MIQ avoids deficit language and focuses on nourishment.");
    }
    
    return guardrails.length > 0 ? guardrails : null;
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 7: Confidence & Next Step
  // ═══════════════════════════════════════════════════════════════════════════
  const getNextStep = () => {
    // Golden Path logic
    if (!hasProfile) {
      return { action: 'Complete Profile', route: 'profile' };
    }
    if (!hasGoals) {
      return { action: 'Set Nutrition Goals', route: 'goals' };
    }
    if (totalMealsLogged < 3) {
      return { action: 'Log More Meals', route: 'camera' };
    }
    if (!hasIFData) {
      return { action: 'Check out MIQ Rhythm tab', route: null };
    }
    return { action: 'View MIQ Dashboard', route: 'miq' };
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // FEEDBACK HANDLER
  // ═══════════════════════════════════════════════════════════════════════════
  const handleFeedback = async (type) => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    setFeedbackType(type);
    setFeedbackSubmitted(true);
    
    // Prepare context for the backend
    const confidence = typeof miqConfidence === 'object' 
      ? (miqConfidence?.percentage || miqConfidence?.overall || 35) 
      : (miqConfidence || 35);
    
    const feedbackContext = {
      brief_version: '1.0',
      confidence_at_time: confidence,
      factors_active: miqConfidence?.factors_computed || 0
    };
    
    try {
      // Call backend API to store feedback
      if (BACKEND_URL) {
        const response = await fetch(`${BACKEND_URL}/api/miq/brief-feedback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            feedback_type: type,
            context: feedbackContext
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('[MIQ Brief] Feedback stored successfully:', data);
          
          // Show non-blocking toast
          if (onShowToast) {
            onShowToast('Thanks — MIQ will calibrate from this.', 'success');
          }
          
          // Also notify parent (legacy callback)
          if (onFeedbackSubmit) {
            onFeedbackSubmit(type);
          }
          
          // Smart routing for "mostly" or "not_quite" feedback
          if (type === 'mostly' || type === 'not_quite') {
            const routing = data.routing_recommendation;
            
            setTimeout(() => {
              if (routing?.route === 'goals' && onOpenSmartGoals) {
                onOpenSmartGoals();
              } else if (routing?.route === 'profile' && setShowOnboarding) {
                setShowOnboarding(true);
              } else if (routing?.route === 'daily_checkin' && setShowDailyCheckin) {
                setShowDailyCheckin(true);
              } else if (onNavigateToInput) {
                // Fallback to legacy routing
                if (type === 'mostly') {
                  onNavigateToInput('profile');
                } else {
                  onNavigateToInput('goals');
                }
              }
            }, 1500);
          }
          // If "accurate", stay on MIQ tab (no routing)
        } else {
          console.error('[MIQ Brief] Failed to store feedback:', response.status);
        }
      }
    } catch (error) {
      console.error('[MIQ Brief] Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
    
    // Also store locally as backup
    localStorage.setItem(`miq_brief_feedback_${userId}`, JSON.stringify({
      type,
      timestamp: Date.now()
    }));
  };
  
  // Get all data
  const optimizationSummary = getOptimizationSummary();
  const focusAreas = getFocusAreas();
  const metabolicContext = getMetabolicContext();
  const geneticContext = getGeneticContext();
  const mealTiming = getMealTimingInsights();
  const medicalGuardrails = getMedicalGuardrails();
  const nextStep = getNextStep();
  const personalizedTips = getPersonalizedTips();
  // Handle confidence as object or number
  const confidence = typeof miqConfidence === 'object' 
    ? (miqConfidence?.percentage || miqConfidence?.overall || 35) 
    : (miqConfidence || 35);
  
  return (
    <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <h2 style={{ 
          margin: 0, 
          fontSize: '18px', 
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          📊 Weekly MetaBolicIQ Intelligence Brief
        </h2>
        <span style={{
          fontSize: '10px',
          background: 'rgba(102, 126, 234, 0.15)',
          color: '#667eea',
          padding: '4px 10px',
          borderRadius: '10px',
          fontWeight: '600'
        }}>
          Read-Only • Updates as MIQ learns
        </span>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SECTION 0: YOUR WEEK IN NUMBERS (Dynamic Data) */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {weeklyInsightsLoading ? (
        <div style={{ 
          padding: '20px',
          background: 'rgba(102, 126, 234, 0.05)',
          borderRadius: '12px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <div style={{ 
            width: '24px', 
            height: '24px', 
            border: '2px solid rgba(102, 126, 234, 0.2)',
            borderTopColor: '#667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 8px'
          }} />
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>
            Loading your weekly insights...
          </p>
        </div>
      ) : weeklyInsights && weeklyInsights.stats?.days_logged > 0 ? (
        <div style={{ marginBottom: '24px' }}>
          {/* Week Header */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: '15px', 
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📊 Your Week in Numbers
            </h3>
            <span style={{ 
              fontSize: '11px', 
              color: 'var(--text-secondary)',
              background: 'rgba(255,255,255,0.05)',
              padding: '4px 8px',
              borderRadius: '6px'
            }}>
              {weeklyInsights.week_label}
            </span>
          </div>
          
          {/* Stats Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '10px',
            marginBottom: '16px'
          }}>
            {/* Calories */}
            <div style={{
              padding: '12px',
              background: 'rgba(102, 126, 234, 0.08)',
              borderRadius: '10px',
              border: '1px solid rgba(102, 126, 234, 0.15)'
            }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                Avg Calories/day
              </p>
              <p style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#667eea' }}>
                {weeklyInsights.stats.avg_calories.toLocaleString()}
              </p>
              {weeklyInsights.stats.targets?.calories > 0 && (
                <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: 'var(--text-secondary)' }}>
                  Target: {weeklyInsights.stats.targets.calories.toLocaleString()}
                  {weeklyInsights.stats.avg_calories <= weeklyInsights.stats.targets.calories ? ' ✓' : ''}
                </p>
              )}
              {weeklyInsights.vs_last_week?.calories_diff !== 0 && (
                <p style={{ 
                  margin: '4px 0 0 0', 
                  fontSize: '10px', 
                  color: weeklyInsights.vs_last_week.calories_diff > 0 ? '#ff9800' : '#4caf50'
                }}>
                  {weeklyInsights.vs_last_week.calories_diff > 0 ? '↑' : '↓'} {Math.abs(weeklyInsights.vs_last_week.calories_diff)} vs last week
                </p>
              )}
            </div>
            
            {/* Protein */}
            <div style={{
              padding: '12px',
              background: 'rgba(76, 175, 80, 0.08)',
              borderRadius: '10px',
              border: '1px solid rgba(76, 175, 80, 0.15)'
            }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                Avg Protein/day
              </p>
              <p style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#4caf50' }}>
                {weeklyInsights.stats.avg_protein}g
              </p>
              {weeklyInsights.stats.targets?.protein > 0 && (
                <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: 'var(--text-secondary)' }}>
                  {Math.round((weeklyInsights.stats.avg_protein / weeklyInsights.stats.targets.protein) * 100)}% of {weeklyInsights.stats.targets.protein}g goal
                </p>
              )}
            </div>
            
            {/* Consistency */}
            <div style={{
              padding: '12px',
              background: weeklyInsights.stats.days_logged >= 5 
                ? 'rgba(255, 152, 0, 0.08)' 
                : 'rgba(255,255,255,0.03)',
              borderRadius: '10px',
              border: weeklyInsights.stats.days_logged >= 5 
                ? '1px solid rgba(255, 152, 0, 0.2)'
                : '1px solid rgba(255,255,255,0.08)'
            }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                Days Logged
              </p>
              <p style={{ 
                margin: 0, 
                fontSize: '20px', 
                fontWeight: '700', 
                color: weeklyInsights.stats.days_logged >= 5 ? '#ff9800' : 'var(--text-primary)'
              }}>
                {weeklyInsights.stats.days_logged}/7
                {weeklyInsights.stats.days_logged >= 5 && ' 🔥'}
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: 'var(--text-secondary)' }}>
                {weeklyInsights.stats.meals_per_day} meals/day avg
              </p>
            </div>
            
            {/* Meal Timing */}
            <div style={{
              padding: '12px',
              background: 'rgba(156, 39, 176, 0.08)',
              borderRadius: '10px',
              border: '1px solid rgba(156, 39, 176, 0.15)'
            }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                First Meal Avg
              </p>
              <p style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#9c27b0' }}>
                {weeklyInsights.stats.avg_first_meal_hour != null
                  ? (() => {
                      const h24 = Math.floor(weeklyInsights.stats.avg_first_meal_hour);
                      const mins = String(Math.round((weeklyInsights.stats.avg_first_meal_hour % 1) * 60)).padStart(2, '0');
                      const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
                      return `${h12}:${mins}`;
                    })()
                  : '--:--'
                }
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: 'var(--text-secondary)' }}>
                {weeklyInsights.stats.avg_first_meal_hour != null
                  ? (weeklyInsights.stats.avg_first_meal_hour < 12 ? 'AM' : 'PM')
                  : 'Log meals with times'
                }
              </p>
            </div>
          </div>
          
          {/* Patterns Detected */}
          {weeklyInsights.patterns && weeklyInsights.patterns.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ 
                margin: '0 0 10px 0', 
                fontSize: '13px', 
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                💡 MIQ Noticed
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {weeklyInsights.patterns.map((pattern, idx) => (
                  <div key={idx} style={{
                    padding: '10px 12px',
                    background: pattern.type === 'positive' 
                      ? 'rgba(76, 175, 80, 0.08)'
                      : pattern.type === 'attention'
                        ? 'rgba(255, 152, 0, 0.08)'
                        : 'rgba(255,255,255,0.03)',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: 'var(--text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>{pattern.icon}</span>
                    <span>{pattern.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* AI Tip for This Week */}
          {weeklyInsights.ai_tip && (
            <div style={{
              padding: '16px',
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              borderRadius: '12px',
              border: '1px solid rgba(102, 126, 234, 0.2)'
            }}>
              <h4 style={{ 
                margin: '0 0 10px 0', 
                fontSize: '13px', 
                fontWeight: '700',
                color: '#667eea',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                🎯 Your Focus This Week
              </h4>
              <p style={{ 
                margin: '0 0 12px 0', 
                fontSize: '13px', 
                color: 'var(--text-primary)',
                lineHeight: '1.6'
              }}>
                {weeklyInsights.ai_tip.tip}
              </p>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                background: 'rgba(102, 126, 234, 0.15)',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: '600',
                color: '#667eea'
              }}>
                <span>📌</span>
                <span>Focus: {weeklyInsights.ai_tip.focus}</span>
              </div>
            </div>
          )}
        </div>
      ) : weeklyInsights && weeklyInsights.stats?.days_logged === 0 ? (
        <div style={{
          padding: '20px',
          background: 'rgba(255, 193, 7, 0.08)',
          borderRadius: '12px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>
            📊 No meals logged this week yet
          </p>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>
            Log meals to unlock your weekly insights and personalized tips
          </p>
        </div>
      ) : null}
      
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SECTION: Daily Check-in Insights (Sleep, Stress, Activity) */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {weeklyInsights?.checkins && weeklyInsights.checkins.days_checked_in > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ 
            margin: '0 0 12px 0', 
            fontSize: '15px', 
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            🧘 Your Weekly Wellness
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '10px'
          }}>
            {/* Sleep */}
            {weeklyInsights.checkins.sleep?.days_logged > 0 && (
              <div style={{
                padding: '12px',
                background: 'rgba(103, 58, 183, 0.08)',
                borderRadius: '10px',
                border: '1px solid rgba(103, 58, 183, 0.15)'
              }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  😴 Sleep
                </p>
                {weeklyInsights.checkins.sleep.avg_hours ? (
                  <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#673ab7' }}>
                    {weeklyInsights.checkins.sleep.avg_hours}h avg
                  </p>
                ) : weeklyInsights.checkins.sleep.quality_label ? (
                  <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#673ab7' }}>
                    {weeklyInsights.checkins.sleep.quality_label}
                  </p>
                ) : null}
                {weeklyInsights.checkins.sleep.quality_label && weeklyInsights.checkins.sleep.avg_hours && (
                  <p style={{ 
                    margin: '4px 0 0 0', 
                    fontSize: '10px', 
                    color: weeklyInsights.checkins.sleep.quality_label === 'Good' || weeklyInsights.checkins.sleep.quality_label === 'Excellent' 
                      ? '#4caf50' 
                      : 'var(--text-secondary)'
                  }}>
                    Quality: {weeklyInsights.checkins.sleep.quality_label}
                    {weeklyInsights.checkins.sleep.avg_quality && ` (${weeklyInsights.checkins.sleep.avg_quality}/4)`}
                  </p>
                )}
                {!weeklyInsights.checkins.sleep.avg_hours && weeklyInsights.checkins.sleep.avg_quality && (
                  <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: 'var(--text-secondary)' }}>
                    Score: {weeklyInsights.checkins.sleep.avg_quality}/4
                  </p>
                )}
                <p style={{ margin: '4px 0 0 0', fontSize: '9px', color: 'var(--text-muted)' }}>
                  {weeklyInsights.checkins.sleep.days_logged} day{weeklyInsights.checkins.sleep.days_logged !== 1 ? 's' : ''} tracked
                </p>
              </div>
            )}
            
            {/* Stress */}
            {weeklyInsights.checkins.stress?.days_logged > 0 && (
              <div style={{
                padding: '12px',
                background: weeklyInsights.checkins.stress.high_stress_days > 2 
                  ? 'rgba(244, 67, 54, 0.08)' 
                  : 'rgba(76, 175, 80, 0.08)',
                borderRadius: '10px',
                border: weeklyInsights.checkins.stress.high_stress_days > 2
                  ? '1px solid rgba(244, 67, 54, 0.15)'
                  : '1px solid rgba(76, 175, 80, 0.15)'
              }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  🧠 Stress Level
                </p>
                <p style={{ 
                  margin: 0, 
                  fontSize: '18px', 
                  fontWeight: '700', 
                  color: weeklyInsights.checkins.stress.high_stress_days > 2 ? '#f44336' : '#4caf50'
                }}>
                  {weeklyInsights.checkins.stress.level_label}
                </p>
                <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: 'var(--text-secondary)' }}>
                  {weeklyInsights.checkins.stress.high_stress_days} high stress day{weeklyInsights.checkins.stress.high_stress_days !== 1 ? 's' : ''}
                </p>
              </div>
            )}
            
            {/* Activity */}
            {weeklyInsights.checkins.activity?.days_logged > 0 && (
              <div style={{
                padding: '12px',
                background: 'rgba(255, 152, 0, 0.08)',
                borderRadius: '10px',
                border: '1px solid rgba(255, 152, 0, 0.15)'
              }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  🏃 Activity
                </p>
                <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#ff9800' }}>
                  {weeklyInsights.checkins.activity.avg_minutes} min/day
                </p>
                <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: 'var(--text-secondary)' }}>
                  {weeklyInsights.checkins.activity.active_days} active day{weeklyInsights.checkins.activity.active_days !== 1 ? 's' : ''} (30+ min)
                </p>
              </div>
            )}
            
            {/* Hydration */}
            {weeklyInsights.checkins.hydration?.days_logged > 0 && (
              <div style={{
                padding: '12px',
                background: 'rgba(33, 150, 243, 0.08)',
                borderRadius: '10px',
                border: '1px solid rgba(33, 150, 243, 0.15)'
              }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  💧 Hydration
                </p>
                <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#2196f3' }}>
                  {Math.round(weeklyInsights.checkins.hydration.avg_ml / 1000 * 10) / 10}L avg
                </p>
                <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: 'var(--text-secondary)' }}>
                  Hit goal {weeklyInsights.checkins.hydration.goal_met_days}/7 days
                </p>
              </div>
            )}
          </div>
          
          {/* Show prompt if no check-in data */}
          {weeklyInsights.checkins.days_checked_in === 0 && (
            <div style={{
              padding: '16px',
              background: 'rgba(255, 193, 7, 0.08)',
              borderRadius: '10px',
              marginTop: '12px',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>
                💡 Complete daily check-ins in MIQ Rhythm to see sleep, stress & activity insights here
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* HYDRATION CONSISTENCY (Keep this) */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      
      {/* Old "What this means for you" section removed - replaced by dynamic AI tips */}
      
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SECTION 3: Metabolic Body Profile */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {metabolicContext && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ 
            margin: '0 0 10px 0', 
            fontSize: '14px', 
            fontWeight: '600',
            color: 'var(--text-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            🏋️ Metabolic Body Profile
          </h3>
          <p style={{ 
            margin: '0 0 12px 0', 
            fontSize: '12px', 
            color: 'var(--text-secondary)', 
            lineHeight: '1.5' 
          }}>
            Your Metabolic Body Profile reflects how your body tends to respond to energy intake, timing, and activity — based on your goals, behavior patterns, and logged data.
          </p>
          <div style={{
            padding: '14px',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.08)'
          }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              Your metabolic characteristics suggest <strong style={{ color: 'var(--text-primary)' }}>{metabolicContext.characteristic}</strong>.
            </p>
            <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              {metabolicContext.implication}
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              <span style={{ color: '#667eea' }}>Focus:</span> {metabolicContext.focus}
            </p>
          </div>
          <p style={{ 
            margin: '10px 0 0 0', 
            fontSize: '11px', 
            color: 'var(--text-muted)', 
            fontStyle: 'italic',
            lineHeight: '1.4'
          }}>
            This profile evolves as MIQ observes consistency, timing, and portion signals over time.
          </p>
        </div>
      )}
      
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SECTION 4: Genetic Context (Ancestry) */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {geneticContext && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ 
            margin: '0 0 10px 0', 
            fontSize: '14px', 
            fontWeight: '600',
            color: 'var(--text-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            🧬 Genetic Context
          </h3>
          <div style={{
            padding: '14px',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.08)',
            marginBottom: '10px'
          }}>
            {geneticContext.map((observation, index) => (
              <p key={index} style={{ 
                margin: index < geneticContext.length - 1 ? '0 0 10px 0' : 0, 
                fontSize: '13px', 
                color: 'var(--text-secondary)', 
                lineHeight: '1.5',
                paddingLeft: '12px',
                borderLeft: '2px solid rgba(102, 126, 234, 0.3)'
              }}>
                {observation}
              </p>
            ))}
          </div>
          {/* Mandatory Disclaimer */}
          <div style={{
            padding: '12px',
            background: 'rgba(255, 193, 7, 0.08)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 193, 7, 0.2)'
          }}>
            <p style={{ 
              margin: 0, 
              fontSize: '11px', 
              color: 'rgba(255, 193, 7, 0.9)', 
              lineHeight: '1.5',
              fontStyle: 'italic'
            }}>
              ⚠️ Ancestry and body context are used for nutritional relevance only. This is not a diagnosis or medical advice.
            </p>
          </div>
        </div>
      )}
      
      {/* Old "Meal Timing & Portion Signals" section removed - redundant with new weekly insights */}
      
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SECTION 6: Medical Guardrails */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {medicalGuardrails && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ 
            margin: '0 0 10px 0', 
            fontSize: '14px', 
            fontWeight: '600',
            color: 'var(--text-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            🛡️ Medical Guardrails Active
          </h3>
          <div style={{
            padding: '14px',
            background: 'rgba(76, 175, 80, 0.08)',
            borderRadius: '10px',
            border: '1px solid rgba(76, 175, 80, 0.2)'
          }}>
            {medicalGuardrails.map((guardrail, index) => (
              <p key={index} style={{ 
                margin: index < medicalGuardrails.length - 1 ? '0 0 10px 0' : 0, 
                fontSize: '13px', 
                color: 'var(--text-secondary)', 
                lineHeight: '1.5' 
              }}>
                {guardrail}
              </p>
            ))}
          </div>
        </div>
      )}
      
      {/* Hydration Narrative removed - now shown in Weekly Wellness grid */}
      
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SECTION 7: Confidence & Next Step */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px',
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          borderRadius: '12px',
          border: '1px solid rgba(102, 126, 234, 0.2)'
        }}>
          <div>
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              MIQ Confidence
            </p>
            <p style={{ margin: '0 0 4px 0', fontSize: '24px', fontWeight: '700', color: '#667eea' }}>
              {confidence}%
            </p>
            <p style={{ margin: 0, fontSize: '10px', color: 'var(--text-secondary)', opacity: 0.8, maxWidth: '140px', lineHeight: '1.4' }}>
              How well MIQ understands you — not how well you&apos;re doing.
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Next Step
            </p>
            {nextStep.route ? (
              <button
                onClick={() => onNavigateToInput && onNavigateToInput(nextStep.route)}
                style={{
                  padding: '10px 16px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {nextStep.action} →
              </button>
            ) : (
              <span
                style={{
                  display: 'inline-block',
                  padding: '10px 16px',
                  background: 'rgba(102, 126, 234, 0.15)',
                  color: '#667eea',
                  border: '1px solid rgba(102, 126, 234, 0.3)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                {nextStep.action}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* FEEDBACK LOOP */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div style={{
        padding: '16px',
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.08)',
        textAlign: 'center'
      }}>
        {feedbackSubmitted ? (
          <div>
            <p style={{ margin: 0, fontSize: '14px', color: '#4caf50', fontWeight: '500' }}>
              ✅ Thanks for your feedback!
            </p>
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              {feedbackType === 'accurate' && "MIQ confidence slightly boosted."}
              {feedbackType === 'mostly' && "Redirecting to adjust your inputs..."}
              {feedbackType === 'not_quite' && "Redirecting to recalibrate..."}
            </p>
          </div>
        ) : (
          <>
            <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: 'var(--text-primary)', fontWeight: '500' }}>
              Does this feel accurate?
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={() => handleFeedback('accurate')}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ✓ Accurate
              </button>
              <button
                onClick={() => handleFeedback('mostly')}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(255, 193, 7, 0.2)',
                  color: '#ffc107',
                  border: '1px solid rgba(255, 193, 7, 0.3)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ≈ Mostly
              </button>
              <button
                onClick={() => handleFeedback('not_quite')}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(244, 67, 54, 0.15)',
                  color: '#f44336',
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ✗ Not quite
              </button>
            </div>
            <p style={{ margin: '10px 0 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
              Your feedback helps MIQ calibrate better.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default MIQIntelligenceBrief;
